import React from 'react';
import {
  useTable,
  useSortBy,
  TableOptions,
  Cell,
  IdType,
  SortingRule,
  useRowSelect,
  Row,
} from 'react-table';

interface ResultsTableOptions<T extends object> extends TableOptions<T> {
  hiddenColumns: IdType<T>[];
  sortBy: Array<SortingRule<T>>;
  getCustomCellProps?: (cell: Cell<T, any>) => object;
  showCheckboxPerRow?: Function;
  markSelectedRowsByCondition?: Function;
}

type ResultsTableReturn<T extends object> = {
  isSelectionVisible: boolean;
  toggleSelection: (value?: boolean) => void;
  selectedRows: Row<T>[];
  render: React.ReactElement;
  toggleAllSelectedRows: (value?: boolean) => void;
};

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }: any, ref) => {
    const defaultRef: any = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <input type='checkbox' ref={resolvedRef} {...rest} />
      </>
    );
  }
);

const useResultsTable = <T extends object>(
  options: ResultsTableOptions<T>
): ResultsTableReturn<T> => {
  const columns = React.useMemo(() => options.columns, [options.columns]);
  const data = React.useMemo(() => options.data, [options.data]);

  const instance = useTable(
    {
      columns,
      data,
      disableSortRemove: true,
      initialState: {
        hiddenColumns: [...options.hiddenColumns, 'selection'],
        sortBy: options.sortBy,
      },
    },
    useSortBy,
    useRowSelect,
    (hooks) => {
      hooks.allColumns.push((columns) => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div className='checkbox-header-cell'>
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            </div>
          ),
          Cell: ({ row }: any) => (
            <div className='checkbox-row-cell'>
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            </div>
          ),
        },
        ...columns,
      ]);
    }
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    toggleAllRowsSelected,
    selectedFlatRows,
  } = instance;

  const selectionColumn = allColumns.filter(
    (column) => column.id === 'selection'
  )[0];

  return {
    isSelectionVisible: selectionColumn.isVisible,
    toggleSelection: selectionColumn.toggleHidden,
    selectedRows: options.markSelectedRowsByCondition
      ? options.markSelectedRowsByCondition!(selectedFlatRows)
      : selectedFlatRows,
    toggleAllSelectedRows: toggleAllRowsSelected,
    render: (
      <table
        {...getTableProps({
          className: 'standard-table',
        })}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps([
                    {
                      className:
                        column.id === 'selection'
                          ? options.showCheckboxPerRow &&
                            rows.some((row) => options.showCheckboxPerRow!(row))
                            ? 'show-cb'
                            : 'hide-cb'
                          : column.isSorted
                          ? 'column-sorted'
                          : '',
                    },
                    column.getSortByToggleProps(),
                  ])}
                >
                  {column.render('Header')}
                  <span
                    key={`${column.isSortedDesc}`}
                    className='ml-1 fcolor--primary-brand d-inline-block'
                    style={{ width: 8 }}
                  >
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <i className='fas fa-long-arrow-alt-down'></i>
                      ) : (
                        <i className='fas fa-long-arrow-alt-up'></i>
                      )
                    ) : (
                      <React.Fragment />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                className={
                  row.isSelected &&
                  selectionColumn.isVisible &&
                  options.showCheckboxPerRow &&
                  options.showCheckboxPerRow(row)
                    ? 'selected-row'
                    : ''
                }
              >
                {row.cells.map((cell) => {
                  return (
                    <td
                      className={
                        cell.column.id === 'selection'
                          ? options.showCheckboxPerRow &&
                            options.showCheckboxPerRow(row)
                            ? 'show-cb'
                            : 'hide-cb'
                          : ''
                      }
                      {...cell.getCellProps([
                        { ...options.getCustomCellProps?.(cell) },
                      ])}
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    ),
  };
};

export default useResultsTable;
